import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchPhysicalFilterDimension } from "../api/physical_view_endpoints";

// Dimensions that return all items (no pagination/search)
const STATIC_DIMENSIONS = ["segment_types", "platforms"];

// Default page sizes per dimension
const DEFAULT_LIMITS = {
  franchises: 50,
  cuisines: 50,
  geo_level_1: 50,
  geo_level_2: 50,
  cities: 50,
  postal_codes: 50,
  segment_types: 50,
  platforms: 50,
};

// Maps frontend dimension key → API path segment
const DIMENSION_PATH = {
  franchises: "franchises",
  cuisines: "cuisines",
  geo_level_1: "geo-level-1",
  geo_level_2: "geo-level-2",
  cities: "cities",
  postal_codes: "postal-codes",
  segment_types: "segment-types",
  platforms: "platforms",
};

const ALL_DIMENSIONS = Object.keys(DEFAULT_LIMITS);

const makeDimensionState = () => ({
  items: [],
  hasMore: false,
  page: 1,
  loading: false,
  loaded: false,
  searchTerm: "",
  // true when the last fetch was denied (HTTP 403) — the caller lacks scope
  // for this dimension's country. Drives an inline "unavailable" notice
  // instead of a silently-empty dropdown.
  forbidden: false,
});

const initialState = {
  meta: {
    id_client: null,
    country: null,
    initialized: false,
  },
  dimensions: Object.fromEntries(
    ALL_DIMENSIONS.map((dim) => [dim, makeDimensionState()])
  ),
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchPhysicalFilterDimensionPage = createAsyncThunk(
  "physicalFilterDimensions/fetchPage",
  async ({ dimension, page = 1, search, signal }, { getState, rejectWithValue }) => {
    const { meta } = getState().physicalFilterDimensions;
    const limit = DEFAULT_LIMITS[dimension] ?? 50;
    const pathSegment = DIMENSION_PATH[dimension];

    try {
      const data = await fetchPhysicalFilterDimension({
        id_client: meta.id_client,
        country: meta.country,
        dimension: pathSegment,
        page,
        limit,
        search,
        signal,
      });
      return { dimension, page, data };
    } catch (err) {
      if (err.name === "AbortError" || err.code === "ERR_CANCELED") {
        return rejectWithValue({ dimension, aborted: true });
      }
      return rejectWithValue({
        dimension,
        error: err.message,
        forbidden: err.status === 403,
      });
    }
  }
);

export const fetchMorePhysicalFilterDimension = createAsyncThunk(
  "physicalFilterDimensions/fetchMore",
  async ({ dimension }, { getState, dispatch }) => {
    const dimState = getState().physicalFilterDimensions.dimensions[dimension];
    if (!dimState?.hasMore || dimState?.loading) return;
    const nextPage = dimState.page + 1;
    return dispatch(
      fetchPhysicalFilterDimensionPage({
        dimension,
        page: nextPage,
        search: dimState.searchTerm || undefined,
      })
    );
  }
);

export const searchPhysicalFilterDimension = createAsyncThunk(
  "physicalFilterDimensions/search",
  async ({ dimension, search }, { dispatch }) => {
    return dispatch(
      fetchPhysicalFilterDimensionPage({
        dimension,
        page: 1,
        search: search || undefined,
      })
    );
  }
);

export const initializePhysicalFilterDimensions = createAsyncThunk(
  "physicalFilterDimensions/initialize",
  async ({ id_client, country }, { dispatch, getState }) => {
    const { meta } = getState().physicalFilterDimensions;
    // Reset if client/country changed
    if (meta.id_client !== id_client || meta.country !== country) {
      dispatch(physicalFilterDimensionsSlice.actions.resetDimensions({ id_client, country }));
    }
    await Promise.all(
      ALL_DIMENSIONS.map((dim) =>
        dispatch(fetchPhysicalFilterDimensionPage({ dimension: dim, page: 1 }))
      )
    );
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const physicalFilterDimensionsSlice = createSlice({
  name: "physicalFilterDimensions",
  initialState,
  reducers: {
    resetDimensions: (state, action) => {
      const { id_client, country } = action.payload;
      state.meta.id_client = id_client;
      state.meta.country = country;
      state.meta.initialized = false;
      ALL_DIMENSIONS.forEach((dim) => {
        state.dimensions[dim] = makeDimensionState();
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPhysicalFilterDimensionPage.pending, (state, action) => {
        const { dimension, page } = action.meta.arg;
        if (state.dimensions[dimension]) {
          state.dimensions[dimension].loading = true;
          // Clear any prior denial so a re-fetch (e.g. after switching to a
          // country the caller is scoped for) can recover.
          state.dimensions[dimension].forbidden = false;
          // If starting a fresh load (page 1 from search), reset items
          if (page === 1) {
            state.dimensions[dimension].items = [];
            state.dimensions[dimension].searchTerm =
              action.meta.arg.search || "";
          }
        }
      })
      .addCase(fetchPhysicalFilterDimensionPage.fulfilled, (state, action) => {
        if (!action.payload) return;
        const { dimension, page, data } = action.payload;
        if (!state.dimensions[dimension]) return;
        const dim = state.dimensions[dimension];
        dim.loading = false;
        dim.loaded = true;
        if (page === 1) {
          dim.items = data.items ?? [];
        } else {
          dim.items = [...dim.items, ...(data.items ?? [])];
        }
        dim.hasMore = data.has_more ?? false;
        dim.page = page;
      })
      .addCase(fetchPhysicalFilterDimensionPage.rejected, (state, action) => {
        const { dimension, aborted, forbidden } = action.payload ?? {};
        if (dimension && state.dimensions[dimension] && !aborted) {
          state.dimensions[dimension].loading = false;
          state.dimensions[dimension].forbidden = !!forbidden;
        }
      })
      .addCase(initializePhysicalFilterDimensions.fulfilled, (state) => {
        state.meta.initialized = true;
      });
  },
});

export const { resetDimensions } = physicalFilterDimensionsSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectPhysicalFilterDimension = (dimension) => (state) =>
  state.physicalFilterDimensions?.dimensions?.[dimension];

export const selectPhysicalFilterMeta = (state) =>
  state.physicalFilterDimensions?.meta;

export default physicalFilterDimensionsSlice.reducer;
